// Hand-written, not generated: a number field with − and + beside it, for
// entering a set on a phone (Training's logging sheet, D9). The field is a
// text input with `inputMode` (a decimal keypad for loads, a numeric one
// for reps) rather than type=number, which eats partial input like "132."
// and has no decimal keypad on iOS. The typed text is the value — the
// buttons step it on the step's grid (lib/decimal.js) and hand back text.
// Each control is 44px on a phone; − and + are named after the field
// ("Decrease load", "Increase load"), never two bare "+" buttons.
import { forwardRef, useId } from "react";
import PropTypes from "prop-types";
import { MinusIcon, PlusIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { parseDecimal, stepDecimal } from "@/lib/decimal";

const Stepper = forwardRef(function Stepper(
  {
    label,
    value,
    onChange,
    step = 1,
    min,
    max,
    inputMode = "decimal",
    unit,
    helper,
    error,
    placeholder,
    decreaseLabel,
    increaseLabel,
    disabled = false,
    id: givenId,
    className,
    ...props
  },
  ref,
) {
  const autoId = useId();
  const id = givenId || autoId;
  const text = value === null || value === undefined ? "" : String(value);
  const current = parseDecimal(text);
  const named = label.toLowerCase();
  const fewer = decreaseLabel || `Decrease ${named}`;
  const more = increaseLabel || `Increase ${named}`;
  const canStep = step > 0 && !disabled;
  const atMin = current !== null && min !== undefined && min !== null && current <= min;
  const atMax = current !== null && max !== undefined && max !== null && current >= max;

  const move = (direction) => {
    const next = stepDecimal(text, step, direction, { min, max });
    if (next !== null) onChange(String(next));
  };

  const describedBy = [unit && `${id}-unit`, helper && !error && `${id}-help`, error && `${id}-error`]
    .filter(Boolean)
    .join(" ");

  return (
    <div data-slot="stepper" className={cn("flex flex-col gap-1.5", className)}>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-11 shrink-0 md:size-9"
          aria-label={fewer}
          title={fewer}
          disabled={!canStep || atMin}
          onClick={() => move(-1)}
        >
          <MinusIcon aria-hidden="true" />
        </Button>
        <Input
          ref={ref}
          id={id}
          type="text"
          inputMode={inputMode}
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="done"
          value={text}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy || undefined}
          className="h-11 min-w-0 flex-1 text-center tabular-nums md:h-9"
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={(event) => {
            // Arrow keys step too, as on a native number field.
            if (!canStep) return;
            if (event.key === "ArrowUp" && !atMax) {
              event.preventDefault();
              move(1);
            } else if (event.key === "ArrowDown" && !atMin) {
              event.preventDefault();
              move(-1);
            }
          }}
          {...props}
        />
        {unit && (
          <span id={`${id}-unit`} className="shrink-0 text-sm text-muted-foreground">
            {unit}
          </span>
        )}
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="size-11 shrink-0 md:size-9"
          aria-label={more}
          title={more}
          disabled={!canStep || atMax}
          onClick={() => move(1)}
        >
          <PlusIcon aria-hidden="true" />
        </Button>
      </div>
      {helper && !error && (
        <p id={`${id}-help`} className="m-0 text-xs text-muted-foreground">
          {helper}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="m-0 text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
});

Stepper.propTypes = {
  /** The field's name, shown and spoken: "Load", "Reps", "Reps in reserve". */
  label: PropTypes.string.isRequired,
  /** The typed text (or a number); "" when empty. */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** Called with the new text — typed, or stepped. */
  onChange: PropTypes.func.isRequired,
  /** What − and + move by; 0 disables them (typing still works). */
  step: PropTypes.number,
  min: PropTypes.number,
  max: PropTypes.number,
  /** "decimal" (loads) or "numeric" (reps, reserve). */
  inputMode: PropTypes.oneOf(["decimal", "numeric"]),
  /** Shown after the field and read as its description ("lb"). */
  unit: PropTypes.string,
  /** What to type, when there's no error. */
  helper: PropTypes.node,
  /** The server's (or the form's) words; marks the field invalid. */
  error: PropTypes.node,
  placeholder: PropTypes.string,
  decreaseLabel: PropTypes.string,
  increaseLabel: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  className: PropTypes.string,
};

export { Stepper };
